import { formatMessage } from '@formosa/shared/i18n/formatMessage';
import { resolveApiErrorMessage } from '@formosa/shared/i18n/resolveApiErrorMessage';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  groupTripMemberFacingLabel,
  travelMemberProductDisplayName,
} from '../member-identity/memberIdentityPresentation';

const text = (value, fallback = '') => (typeof value === 'string' && value.trim() ? value.trim() : fallback);

/** Opaque snowflake-safe ID for GroupTrip HTTP/JS boundary（不經 Number 轉換）。 */
export function opaqueId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return String(value);
}

const pad2 = (value) => String(value).padStart(2, '0');

/**
 * Normalize backend LocalDateTime wall-clock values for datetime-local.
 * Accepts ISO local strings and Jackson array form [y,m,d,h,mi,(s?)].
 * Does not use Date timezone conversion.
 */
export function normalizeLocalDateTime(value) {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) {
    if (value.length < 5) return '';
    const [year, month, day, hour, minute] = value;
    if ([year, month, day, hour, minute].some((part) => part == null || Number.isNaN(Number(part)))) {
      return '';
    }
    return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
  }
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    return match ? `${match[1]}T${match[2]}` : '';
  }
  return '';
}

/** Backend LocalDateTime → datetime-local（純字串／陣列，不做 timezone 轉換）。 */
export function toDatetimeLocalValue(localDateTimeValue) {
  return normalizeLocalDateTime(localDateTimeValue);
}

/** datetime-local → backend LocalDateTime PATCH string（wall-clock，append :00 seconds）。 */
export function toLocalDateTimePayload(value) {
  if (!value || typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return match ? `${match[1]}T${match[2]}:00` : null;
}

/** @deprecated use toLocalDateTimePayload */
export function toIsoDateTime(value) {
  return toLocalDateTimePayload(value);
}

export function occupancyCopy({
  capacity,
  occupiedCapacity,
  availableSlots,
  reservedCapacity,
  availableCommerceSlots,
}, t, tf) {
  const cap = Number(capacity) || 0;
  const occupied = Number(occupiedCapacity) || 0;
  const available = Number(availableSlots);
  const slots = Number.isFinite(available) ? available : Math.max(0, cap - occupied);
  const reserved = Number(reservedCapacity) || 0;
  const commerce = Number.isFinite(Number(availableCommerceSlots))
    ? Number(availableCommerceSlots)
    : Math.max(0, cap - occupied - reserved);
  let copy = slots <= 0
    ? tf('travel.groupTrip.occupancyFull', { cap, occupied })
    : tf('travel.groupTrip.occupancyBase', { cap, occupied, slots });
  if (reserved > 0 || Number.isFinite(Number(availableCommerceSlots))) {
    copy += tf('travel.groupTrip.occupancyReserved', { reserved, commerce });
  }
  return copy;
}

export function normalizeGroupTrip(value, t) {
  const translate = t || ((key) => key);
  const occupiedCapacity = Number(value?.occupiedCapacity) || 0;
  const capacity = Number(value?.capacity) || 0;
  const reservedCapacity = Number(value?.reservedCapacity) || 0;
  return {
    groupTripId: opaqueId(value?.groupTripId),
    hostMemberId: opaqueId(value?.hostMemberId),
    journeyPublicationId: opaqueId(value?.journeyPublicationId),
    recruitmentTitle: text(value?.recruitmentTitle, translate('travel.groupTrip.defaultTitle')),
    summary: text(value?.summary),
    capacity,
    occupiedCapacity,
    availableSlots: Number.isFinite(Number(value?.availableSlots))
      ? Number(value.availableSlots)
      : Math.max(0, capacity - occupiedCapacity),
    reservedCapacity,
    availableCommerceSlots: Number.isFinite(Number(value?.availableCommerceSlots))
      ? Number(value.availableCommerceSlots)
      : Math.max(0, capacity - occupiedCapacity - reservedCapacity),
    displayPrice: value?.displayPrice ?? null,
    currencyCode: value?.currencyCode || '',
    startAt: value?.startAt ?? null,
    endAt: value?.endAt ?? null,
    visibility: value?.visibility || 'PUBLIC',
    recruitmentStatus: value?.recruitmentStatus || 'DRAFT',
    rowVersion: value?.rowVersion,
    viewerApplicationId: opaqueId(value?.viewerApplicationId),
    viewerApplicationStatus: value?.viewerApplicationStatus || null,
    viewerParticipantId: opaqueId(value?.viewerParticipantId),
    viewerParticipantStatus: value?.viewerParticipantStatus || null,
    viewerTravelTradeId: opaqueId(value?.viewerTravelTradeId),
    viewerTravelTradeStatus: value?.viewerTravelTradeStatus || null,
    tripPhase: value?.tripPhase || null,
    hostReputation: normalizeHostReputation(value?.hostReputation),
    host: normalizeGroupTripHost(value?.host, opaqueId(value?.hostMemberId)),
  };
}

/**
 * Nested host summary from public list/detail. IDs stay opaque strings.
 * profileAvailable gates Creator Profile navigation — never invent a link for unreadable hosts.
 */
export function normalizeGroupTripHost(value, fallbackMemberId = null) {
  const memberId = opaqueId(value?.memberId) || fallbackMemberId;
  const profileAvailable = Boolean(value?.profileAvailable) && memberId != null;
  const displayName = profileAvailable
    ? travelMemberProductDisplayName({
      displayName: value?.displayName,
      nickname: value?.nickname,
      username: value?.username,
    }, { genericFallback: groupTripMemberFacingLabel({ role: 'host' }) })
    : groupTripMemberFacingLabel({ role: 'host' });
  return {
    memberId,
    displayName,
    avatarUrl: profileAvailable ? resolveMemberMediaUrl(value?.avatarUrl) : null,
    profileAvailable,
  };
}

function normalizeHostReputation(value) {
  const reviewCount = Number(value?.reviewCount) || 0;
  if (reviewCount <= 0) {
    return { averageRating: null, reviewCount: 0 };
  }
  return {
    averageRating: value?.averageRating ?? null,
    reviewCount,
  };
}

export function normalizeGroupTripList(payload, t) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map((item) => normalizeGroupTrip(item, t)).filter((item) => item.groupTripId != null)
    : [];
  return { items };
}

export function normalizeApplication(value) {
  const party = Array.isArray(value?.party)
    ? value.party.map((row) => ({
      declarationId: opaqueId(row?.declarationId),
      displayName: text(row?.displayName),
      applicantSelf: Boolean(row?.applicantSelf),
      displayOrder: Number(row?.displayOrder) || 0,
    }))
    : [];
  return {
    applicationId: opaqueId(value?.applicationId),
    groupTripId: opaqueId(value?.groupTripId),
    applicantMemberId: opaqueId(value?.applicantMemberId),
    status: value?.status || 'PENDING',
    applicationNote: text(value?.applicationNote),
    rowVersion: value?.rowVersion,
    party,
    requestedSeats: party.length,
  };
}

export function buildGroupTripApplyPayload({ applicationNote, selfDisplayName, companionNames }) {
  const companions = (companionNames || []).map((name) => String(name || '').trim()).filter(Boolean);
  const selfName = String(selfDisplayName || '').trim();
  const body = { applicationNote: applicationNote || null };
  if (selfName || companions.length > 0) {
    body.party = [
      { displayName: selfName || '本人' },
      ...companions.map((displayName) => ({ displayName })),
    ];
  }
  return body;
}

export function normalizeTraveler(value) {
  return {
    travelerId: opaqueId(value?.travelerId),
    groupTripId: opaqueId(value?.groupTripId),
    displayName: text(value?.displayName),
    linkedMemberId: opaqueId(value?.linkedMemberId),
    status: value?.status || 'ACTIVE',
    guest: Boolean(value?.guest),
    effectiveJoinAt: value?.effectiveJoinAt || null,
    joinSource: ['NORMAL', 'MID_TRIP', 'REPLACEMENT'].includes(value?.joinSource) ? value.joinSource : 'UNKNOWN',
  };
}

const PARTICIPATION_LABEL_KEYS = {
  EXPECTED: 'travel.groupTrip.participationExpected',
  TEMPORARILY_AWAY: 'travel.groupTrip.participationAway',
  SKIPPED: 'travel.groupTrip.participationSkipped',
  ATTENDED: 'travel.groupTrip.participationAttended',
  NOT_PARTICIPATING: 'travel.groupTrip.participationNotJoining',
  NOT_PARTICIPATING_DUE_TO_INDEPENDENT_PERIOD: 'travel.groupTrip.participationIndependent',
  NOT_APPLICABLE: 'travel.groupTrip.participationNotApplicable',
  NOT_JOINED_YET: 'travel.groupTrip.lateJoinNotJoinedYet',
};

export function participationStateLabel(state, t, travelerStatus) {
  if (state === 'NOT_PARTICIPATING' && travelerStatus === 'ACTIVE') {
    return t('travel.groupTrip.d1ParticipationEnded');
  }
  const key = PARTICIPATION_LABEL_KEYS[state];
  return key ? t(key) : '';
}

export function normalizeJourneyActivity(value) {
  return {
    journeyActivityId: opaqueId(value?.journeyActivityId),
    title: text(value?.title),
    cancelled: Boolean(value?.cancelled),
  };
}

function optionalLocalDateTime(value) {
  if (value == null || value === '') return null;
  return normalizeLocalDateTime(value) || null;
}

export function normalizeDisruptionTraveler(value) {
  return {
    travelerId: opaqueId(value?.travelerId),
    displayName: text(value?.displayName),
    capacityStatus: value?.capacityStatus || '',
    participationEnded: Boolean(value?.participationEnded),
    openPeriodId: opaqueId(value?.openPeriodId),
    canStartPeriod: Boolean(value?.canStartPeriod),
    canEndPeriod: Boolean(value?.canEndPeriod),
    canFormalEnd: Boolean(value?.canFormalEnd),
    // effective* 來自 backend read model，前端不另算目前是否有效。
    effectivePeriodId: opaqueId(value?.effectivePeriodId),
    effectivePeriodStartAt: optionalLocalDateTime(value?.effectivePeriodStartAt),
    effectivePeriodEndAt: optionalLocalDateTime(value?.effectivePeriodEndAt),
  };
}

export function normalizeDisruptionBoard(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeDisruptionTraveler).filter((item) => item.travelerId != null)
    : [];
  return { items };
}

export function normalizeJourneyActivityList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeJourneyActivity).filter((item) => item.journeyActivityId != null)
    : [];
  return { items };
}

export function normalizeActivityParticipation(value) {
  return {
    travelerId: opaqueId(value?.travelerId),
    displayName: text(value?.displayName),
    linkedMemberId: opaqueId(value?.linkedMemberId),
    guest: Boolean(value?.guest),
    travelerStatus: value?.travelerStatus || '',
    persistedState: value?.persistedState || null,
    effectiveState: value?.effectiveState || '',
    reason: text(value?.reason),
    canUpdate: Boolean(value?.canUpdate),
  };
}

export function normalizeActivityParticipationList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeActivityParticipation).filter((item) => item.travelerId != null)
    : [];
  return { items };
}

export function normalizeTravelerList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeTraveler).filter((item) => item.travelerId != null)
    : [];
  return { items };
}

export function normalizeApplicationList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeApplication).filter((item) => item.applicationId != null)
    : [];
  return { items };
}

export function normalizeParticipant(value) {
  return {
    participantId: opaqueId(value?.participantId),
    memberId: opaqueId(value?.memberId),
    status: value?.status || 'ACTIVE',
    joinedAt: value?.joinedAt || null,
    travelTradeStatus: value?.travelTradeStatus || null,
  };
}

export function normalizeParticipantList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeParticipant).filter((item) => item.participantId != null)
    : [];
  return { items };
}

export function canContactGroupTripHost(trip, viewerId) {
  if (!trip || viewerId == null || String(viewerId) === String(trip.hostMemberId)) return false;
  return trip.viewerApplicationStatus === 'PENDING' || trip.viewerApplicationStatus === 'APPROVED';
}

export function canLeaveGroupTrip(trip) {
  return trip?.viewerParticipantStatus === 'ACTIVE' && trip?.viewerTravelTradeStatus !== 'PAID';
}

export function isPaidGroupTrip(trip) {
  const price = Number(trip?.displayPrice);
  return Number.isFinite(price) && price > 0;
}

/** Approved/active seats are full (Option B soft UX; Apply stays allowed). */
export function isApprovedCapacityFull(trip) {
  const slots = Number(trip?.availableSlots);
  return Number.isFinite(slots) && slots < 1;
}

/**
 * FREE approve hard-gates on availableSlots. PAID approve does not —
 * commerce capacity is enforced later at TravelTrade create.
 */
export function isHostApproveBlockedByCapacity(trip) {
  if (!trip || isPaidGroupTrip(trip)) return false;
  return isApprovedCapacityFull(trip);
}

export function isGroupTripCapacityExceededError(cause) {
  return cause?.raw?.key === 'travel.grouptrip.capacity.exceeded';
}

// ---- FORMOSA-GROUP-TRIP-DATE-CONFLICT：後端 409 95170／95171 與申請回應 dateConflictWarnings ----

const DATE_CONFLICT_KEY = 'travel.grouptrip.date.conflict';
const DATES_LOCKED_KEY = 'travel.grouptrip.dates.locked';
const OTHER_MEMBER_DATE_CONFLICT_KEY = 'travel.grouptrip.date.conflict_other';
const DATE_CHECK_BUSY_KEY = 'travel.grouptrip.date.check_busy';
const SNOWFLAKE_TEXT = /^\d{1,20}$/;
const PLAIN_DATE_TEXT = /^\d{4}-\d{2}-\d{2}$/;

/** 日期檢查忙碌（95173；舊版後端為 95170 加空 args）：沒有完成檢查，可稍後重試。 */
export function isGroupTripDateCheckBusyError(cause) {
  return cause?.raw?.key === DATE_CHECK_BUSY_KEY || cause?.raw?.code === 95173;
}

/** 本人路徑的日期衝突家族：95170 衝突，以及 95173 忙碌（後者再由 isGroupTripDateConflictRetryable 區分）。 */
export function isGroupTripDateConflictError(cause) {
  return cause?.raw?.key === DATE_CONFLICT_KEY || cause?.raw?.code === 95170 || isGroupTripDateCheckBusyError(cause);
}

/** 主揪核准他人時的衝突（95172）：回應不含對方的揪團明細。 */
export function isGroupTripOtherMemberDateConflictError(cause) {
  return cause?.raw?.key === OTHER_MEMBER_DATE_CONFLICT_KEY || cause?.raw?.code === 95172;
}

export function isGroupTripDatesLockedError(cause) {
  return cause?.raw?.key === DATES_LOCKED_KEY || cause?.raw?.code === 95171;
}

/** 後端以 ISO 字串輸出；若 mapper 設定改變而輸出 [y, m, d]，仍換成 yyyy-MM-dd。 */
function plainDateText(value) {
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value.map(Number);
    if ([y, m, d].every(Number.isInteger)) {
      return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  return String(value ?? '');
}

function dateConflictEntry(groupTripId, startDate, endDate) {
  const id = groupTripId == null ? '' : String(groupTripId);
  const start = plainDateText(startDate);
  const end = plainDateText(endDate);
  if (!SNOWFLAKE_TEXT.test(id) || !PLAIN_DATE_TEXT.test(start) || !PLAIN_DATE_TEXT.test(end)) return null;
  return { groupTripId: id, startDate: start, endDate: end };
}

function uniqueEntries(entries) {
  const seen = new Set();
  return entries.filter((entry) => {
    if (!entry || seen.has(entry.groupTripId)) return false;
    seen.add(entry.groupTripId);
    return true;
  });
}

/** 409 args：[[groupTripId 字串, startDate, endDate], ...]；格式不符的項目丟棄。空陣列代表日期鎖忙碌、可重試。 */
export function groupTripDateConflicts(cause) {
  const args = cause?.raw?.args;
  if (!Array.isArray(args)) return [];
  return uniqueEntries(args.map((item) => (Array.isArray(item) ? dateConflictEntry(item[0], item[1], item[2]) : null)));
}

/**
 * 後端 args 為空陣列：member 日期鎖逾時，可稍後重試。
 * 以原始 args 判斷；有明細但格式不符時仍是衝突（顯示阻擋、不列行程），不能叫使用者重試。
 */
export function isGroupTripDateConflictRetryable(cause) {
  if (isGroupTripDateCheckBusyError(cause)) return true;
  const args = cause?.raw?.args;
  return (cause?.raw?.key === DATE_CONFLICT_KEY || cause?.raw?.code === 95170) && Array.isArray(args) && args.length === 0;
}

/** 申請回應的待審核重疊提醒；只接受 id 字串＋yyyy-MM-dd。 */
export function groupTripDateConflictWarnings(application) {
  const warnings = application?.dateConflictWarnings;
  if (!Array.isArray(warnings)) return [];
  return uniqueEntries(warnings.map((item) => dateConflictEntry(item?.groupTripId, item?.startDate, item?.endDate)));
}

export function canHostCancelGroupTrip(trip) {
  return trip?.recruitmentStatus === 'DRAFT'
    || trip?.recruitmentStatus === 'OPEN'
    || trip?.recruitmentStatus === 'CLOSED'
    || trip?.recruitmentStatus === 'CANCELLING';
}

export function canBuyerCancelPaidParticipation(trip) {
  if (!trip || trip.viewerTravelTradeStatus !== 'PAID') return false;
  if (trip.viewerParticipantStatus !== 'ACTIVE') return false;
  if (trip.recruitmentStatus === 'CANCELLING' || trip.recruitmentStatus === 'CANCELLED') return false;
  if (trip.recruitmentStatus !== 'OPEN' && trip.recruitmentStatus !== 'CLOSED') return false;
  if (!trip.startAt) return false;
  const startMs = Date.parse(trip.startAt);
  return Number.isFinite(startMs) && Date.now() < startMs;
}

export function isBuyerRefundInProgress(trip, refunding) {
  return Boolean(refunding);
}

export function tripPhaseCopy(trip, t, tf) {
  const phase = trip?.tripPhase;
  if (!phase || typeof t !== 'function') return null;
  const phaseKey = `travel.groupTrip.tripPhase.${phase}`;
  const label = t(phaseKey);
  if (label === phaseKey) return null;
  if (typeof tf === 'function') {
    return tf('travel.groupTrip.tripPhaseLabel', { phase: label });
  }
  return formatMessage(t('travel.groupTrip.tripPhaseLabel'), { phase: label });
}

export function hostCancelActionLabel(trip, t) {
  if (trip?.recruitmentStatus === 'CANCELLED') return t('travel.groupTrip.hostCancelled');
  if (trip?.recruitmentStatus === 'CANCELLING') return t('travel.groupTrip.hostCancelling');
  return t('travel.groupTrip.hostCancel');
}

export function hostCancellationNotice(trip, t) {
  if (trip?.recruitmentStatus === 'CANCELLED') return t('travel.groupTrip.cancelledHint');
  if (trip?.recruitmentStatus === 'CANCELLING') return t('travel.groupTrip.refundProcessing');
  if (isPaidGroupTrip(trip) || Number(trip?.reservedCapacity) > 0) {
    return t('travel.groupTrip.paidRefundWarning');
  }
  return null;
}

export function hasActiveParticipantForMember(participants, memberId) {
  return (participants || []).some(
    (participant) => participant.status === 'ACTIVE' && String(participant.memberId) === String(memberId),
  );
}

export function canRevokeApprovedApplication(application, participants) {
  return application?.status === 'APPROVED'
    && !hasActiveParticipantForMember(participants, application.applicantMemberId);
}

export function isAwaitingPaymentTrade(trip) {
  return trip?.viewerTravelTradeStatus === 'AWAITING_PAYMENT';
}

export function isPaidTrade(trip) {
  return trip?.viewerTravelTradeStatus === 'PAID';
}

/** PAID-backed Participant 不可 ordinary remove；退款只走 Host GroupTrip CANCEL。 */
export function isPaidBackedParticipant(participant) {
  return participant?.travelTradeStatus === 'PAID';
}

export function canHostRemoveParticipant(participant) {
  return participant?.status === 'ACTIVE' && !isPaidBackedParticipant(participant);
}

export function paidParticipantHostHint(participant, t) {
  if (participant?.status !== 'ACTIVE' || !isPaidBackedParticipant(participant)) return null;
  return t('travel.groupTrip.paidCannotRemove');
}

export function groupTripErrorMessage(cause, t, fallbackKey = 'travel.groupTrip.loadFailed') {
  if (typeof t !== 'function') {
    return resolveApiErrorMessage(cause, (key) => key, fallbackKey);
  }
  return resolveApiErrorMessage(cause, t, fallbackKey);
}

export function confirmPayload(rowVersion) {
  return { expectedRowVersion: rowVersion, confirm: true };
}

export function canHostEditGroupTrip(trip) {
  return trip?.recruitmentStatus === 'DRAFT';
}

export function draftFormValuesFromTrip(trip) {
  return {
    recruitmentTitle: trip?.recruitmentTitle || '',
    summary: trip?.summary || '',
    capacity: String(trip?.capacity ?? ''),
    startAt: toDatetimeLocalValue(trip?.startAt),
    endAt: toDatetimeLocalValue(trip?.endAt),
    visibility: trip?.visibility || 'PUBLIC',
  };
}

// Preserve server precision for existing immutable dates (legacy seconds / array nanoseconds).
function immutableDateTimePayload(value) {
  if (typeof value === 'string') return value.trim();
  const minute = normalizeLocalDateTime(value);
  if (!minute) return null;
  const fraction = value[6] ? `.${String(value[6]).padStart(9, '0')}` : '';
  return `${minute}:${pad2(value[5] || 0)}${fraction}`;
}

export function validateGroupTripDraftForm(values, t, lockedTrip = null) {
  const title = text(values.recruitmentTitle);
  const cap = Number(values.capacity);
  const start = lockedTrip ? immutableDateTimePayload(lockedTrip.startAt) : toLocalDateTimePayload(values.startAt);
  const end = lockedTrip ? immutableDateTimePayload(lockedTrip.endAt) : toLocalDateTimePayload(values.endAt);
  if (!title) return t('travel.groupTrip.titleRequired');
  if (!Number.isInteger(cap) || cap < 1 || cap > 10000) return t('travel.groupTrip.capacityInvalid');
  if (!start || !end || end <= start) return t('travel.groupTrip.dateRangeInvalid');
  return '';
}

export function buildUpdateDraftPayload(trip, formValues) {
  const cap = Number(formValues.capacity);
  return {
    expectedRowVersion: trip.rowVersion,
    recruitmentTitle: text(formValues.recruitmentTitle),
    summary: text(formValues.summary) || null,
    capacity: cap,
    displayPrice: trip.displayPrice ?? null,
    currencyCode: trip.currencyCode || null,
    startAt: immutableDateTimePayload(trip.startAt),
    endAt: immutableDateTimePayload(trip.endAt),
    visibility: formValues.visibility,
  };
}

export function classifyGroupTripMutationError(cause) {
  const key = cause?.raw?.key || '';
  if (key === 'travel.grouptrip.concurrent.modification') return 'conflict';
  if (key === 'travel.grouptrip.invalid.lifecycle') return 'lifecycle';
  if (key === 'travel.grouptrip.permission.denied') return 'forbidden';
  if (key === 'travel.grouptrip.not.found') return 'not-found';
  if (key === 'travel.grouptrip.validation.failed') return 'validation';
  return 'unknown';
}

export function newTradeClientRequestId(groupTripId) {
  return `gt-${groupTripId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 測試用：以指定 locale 建立 t / tf */
export function createGroupTripTranslators(locale, translateFn) {
  const t = (key) => translateFn(locale, key);
  const tf = (key, values) => formatMessage(t(key), values);
  return { t, tf };
}
